import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const chip = tailwindVariants({
  base: 'inline-flex items-center justify-between gap-1 truncate font-medium',
  compoundVariants: [
    {
      appearance: 'filled',
      className: 'bg-black text-white',
      color: 'black',
    },
    {
      appearance: 'outline',
      className: 'border-black text-black',
      color: 'black',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-sky-7 bg-blue-5 ads:text-ads-sky-12 text-blue-12',
      color: 'blue',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-sky-8 border-blue-5 ads:text-ads-sky-11 text-blue-9',
      color: 'blue',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-grass-7 bg-green-7 ads:text-ads-grass-12 text-teal-12',
      color: 'green',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-grass-8 border-green-5 ads:text-ads-grass-11 text-green-9',
      color: 'green',
    },
    {
      appearance: 'filled',
      className: 'bg-gray-4 ads:text-primary text-gray-12',
      color: 'gray',
    },
    {
      appearance: 'filled',
      className: 'bg-gray-4 ads:text-primary text-gray-12',
      color: 'grey',
    },
    {
      appearance: 'outline',
      className: 'border-gray-5 ads:text-metadata text-gray-9',
      color: 'gray',
    },
    {
      appearance: 'outline',
      className: 'border-gray-5 ads:text-metadata text-gray-9',
      color: 'grey',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-sky-4 bg-blue-3 ads:text-ads-sky-12 text-blue-12',
      color: 'light-blue',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-sky-6 border-blue-5 ads:text-ads-sky-11 text-blue-9',
      color: 'light-blue',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-grass-4 bg-green-4 ads:text-ads-grass-12 text-green-12',
      color: 'light-green',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-grass-6 border-green-5 ads:text-ads-grass-11 text-green-9',
      color: 'light-green',
    },
    {
      appearance: 'filled',
      className: 'bg-gray-4 ads:text-primary text-gray-12',
      color: 'light-gray',
    },
    {
      appearance: 'filled',
      className: 'bg-gray-4 ads:text-primary text-gray-12',
      color: 'light-grey',
    },
    {
      appearance: 'outline',
      className: 'ads:border-subtle border-gray-3 ads:text-metadata text-gray-9',
      color: 'light-gray',
    },
    {
      appearance: 'outline',
      className: 'ads:border-subtle border-gray-3 ads:text-metadata text-gray-9',
      color: 'light-grey',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-ember-4 bg-orange-2 ads:text-ads-ember-12 text-orange-12',
      color: 'light-orange',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-ember-6 border-orange-3 ads:text-ads-ember-11 text-orange-9',
      color: 'light-orange',
    },
    {
      appearance: 'filled',
      className: 'bg-pink-2 text-pink-12',
      color: 'light-pink',
    },
    {
      appearance: 'outline',
      className: 'border-pink-3 text-pink-9',
      color: 'light-pink',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-violet-6 bg-purple-2 ads:text-ads-violet-12 text-purple-12',
      color: 'light-purple',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-violet-6 border-purple-3 ads:text-ads-violet-11 text-purple-9',
      color: 'light-purple',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-error-subtle bg-red-4 ads:text-error text-red-12',
      color: 'light-red',
    },
    {
      appearance: 'outline',
      className: 'ads:border-error border-red-3 ads:text-error text-red-9',
      color: 'light-red',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-cyan-4 bg-teal-4 ads:text-ads-cyan-12 text-teal-12',
      color: 'light-teal',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-cyan-6 border-teal-3 ads:text-ads-cyan-11 text-teal-9',
      color: 'light-teal',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-amber-4 bg-yellow-3 ads:text-ads-amber-12 text-yellow-12',
      color: 'light-yellow',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-amber-6 border-yellow-3 ads:text-ads-amber-11 text-yellow-9',
      color: 'light-yellow',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-ember-7 bg-orange-4 ads:text-ads-ember-12 text-orange-12',
      color: 'orange',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-ember-8 border-orange-5 ads:text-ads-ember-11 text-orange-9',
      color: 'orange',
    },
    {
      appearance: 'filled',
      className: 'bg-pink-4 text-pink-12',
      color: 'pink',
    },
    {
      appearance: 'outline',
      className: 'border-pink-5 text-pink-9',
      color: 'pink',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-violet-8 bg-purple-6 ads:text-ads-violet-12 text-purple-12',
      color: 'purple',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-violet-8 border-purple-5 ads:text-ads-violet-11 text-purple-9',
      color: 'purple',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-flash-7 bg-red-7 ads:text-ads-flash-12 text-red-12',
      color: 'red',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-flash-8 border-red-5 ads:text-ads-flash-11 text-red-9',
      color: 'red',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-cyan-7 bg-teal-6 ads:text-ads-cyan-12 text-teal-12',
      color: 'teal',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-cyan-8 border-teal-5 ads:text-ads-cyan-11 text-teal-9',
      color: 'teal',
    },
    {
      appearance: 'filled',
      className: 'bg-transparent ads:text-primary text-gray-12',
      color: 'transparent',
    },
    {
      appearance: 'outline',
      className: 'border-transparent ads:text-primary text-gray-12',
      color: 'transparent',
    },
    {
      appearance: 'filled',
      className: 'ads:bg-ads-amber-7 bg-yellow-6 ads:text-ads-amber-12 text-yellow-12',
      color: 'yellow',
    },
    {
      appearance: 'outline',
      className: 'ads:border-ads-amber-8 border-yellow-5 ads:text-ads-amber-11 text-yellow-9',
      color: 'yellow',
    },
    {
      class: 'px-2',
      shape: 'rounded',
      size: 'small',
    },
    {
      class: 'px-3',
      shape: 'rounded',
      size: ['medium', 'large', 'extra-large'],
    },
    {
      className: 'px-1',
      shape: 'rectangular',
      size: 'small',
    },
    {
      className: 'px-1.5',
      shape: 'rectangular',
      size: ['medium', 'large', 'extra-large'],
    },
  ],
  defaultVariants: {
    appearance: 'filled',
    color: 'gray',
    shape: 'rectangular',
    size: 'small',
  },
  variants: {
    appearance: {
      filled: 'border-0',
      outline: 'border',
    },
    color: {
      black: '',
      blue: '',
      gray: '',
      green: '',
      /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */
      grey: '',
      'light-blue': '',
      'light-gray': '',
      'light-green': '',
      /** @deprecated Use 'light-gray' instead. British spelling kept for backwards compatibility (DES-25). */
      'light-grey': '',
      'light-orange': '',
      'light-pink': '',
      'light-purple': '',
      'light-red': '',
      'light-teal': '',
      'light-yellow': '',
      orange: '',
      pink: '',
      purple: '',
      red: '',
      teal: '',
      transparent: '',
      yellow: '',
    },
    shape: {
      rectangular: 'rounded-4',
      rounded: 'rounded-full',
    },
    size: {
      'extra-large': 'h-8 text-14',
      large: 'h-7 text-14',
      medium: 'h-6 text-12',
      small: 'h-5 text-10',
    },
  },
});

export type ChipVariants = VariantProps<typeof chip>;

export type ChipProps = Omit<ComponentPropsWithoutRef<typeof Primitive.div>, keyof ChipVariants> & ChipVariants;

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  ({ appearance, className, color = 'gray', shape = 'rectangular', size = 'small', ...restOfProps }, forwardedRef) => {
    return (
      <Primitive.div
        className={chip({ appearance, className, color, shape, size })}
        data-testid="CHIP"
        {...restOfProps}
        ref={forwardedRef}
      />
    );
  },
);

Chip.displayName = 'Chip';
